/**
 * The tracker replay on a Teslyn Cloud map (Diurnal parity 03-031, T16, decision 75; docs/specs/cloud/
 * live-tracking.md §4), as Teslyn Field's (`apps/field/src/tracker/ReplayBar.tsx`): Play and Pause, the speed, a
 * scrub bar over the trails the map has and the replay time in the viewer's time and UTC, "Back to live". While it is
 * open the map draws the aircraft where they were at that time and their trails up to then. The rules are
 * `@teslyn/domain` `tracker-replay.ts`; nothing here invents a position. Teslyn Field reads its trails from the
 * laptop's feed; a Cloud map passes the trails it read from Teslyn Cloud.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  REPLAY_SPEEDS,
  replayAircraft,
  replayRange,
  replayReducer,
  replaySpeedWords,
  replayTimeWords,
  replayTrails,
  replayUntimed,
  startReplay,
  type AircraftPosition,
  type ReplayClock,
  type ReplayEvent,
  type ReplaySpeed,
  type TrailPoint,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';

/** How often a playing replay moves on, ms of real time (Teslyn Field's). */
export const REPLAY_FRAME_MS = 250;

export interface Replay {
  /** Null when no trail has a timed point in the range. */
  readonly clock: ReplayClock | null;
  /** The map at the replay time; null when there is nothing to replay. */
  readonly frame: {
    readonly aircraft: AircraftPosition[];
    readonly trails: Map<string, TrailPoint[]>;
  } | null;
  /** The aircraft whose trail has no times, which cannot be replayed. */
  readonly untimed: readonly string[];
  readonly dispatch: (e: ReplayEvent) => void;
}

/** The replay of `aircraft` and their `trails` while `active`; null while the map is live. */
export function useReplay(
  aircraft: readonly AircraftPosition[],
  trails: ReadonlyMap<string, readonly TrailPoint[]>,
  trailHours: number,
  active: boolean,
  now: () => number = Date.now,
): Replay | null {
  const [clock, setClock] = useState<ReplayClock | null>(null);
  const latest = useRef(trails);
  latest.current = trails;
  // opened: at the start of the range, paused; asked again when the trails come in
  useEffect(() => {
    if (!active) {
      setClock(null);
      return;
    }
    const range = replayRange(trails, now());
    setClock((c) => c ?? (range ? startReplay(range) : null));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the clock is read when the replay opens
  }, [active, trails]);
  const playing = clock?.playing ?? false;
  // playing: the replay time moves on by the real time passed times the speed; the range follows now
  useEffect(() => {
    if (!active || !playing) return undefined;
    let last = now();
    const id = setInterval(() => {
      const t = now();
      const elapsed = t - last;
      last = t;
      const range = replayRange(latest.current, t);
      setClock((c) => {
        if (!c) return c;
        const moved = range ? replayReducer(c, { type: 'range', range }) : c;
        return replayReducer(moved, { type: 'tick', elapsedMs: elapsed });
      });
    }, REPLAY_FRAME_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the clock is read at each frame
  }, [active, playing]);
  const atMs = clock?.atMs ?? null;
  const frame = useMemo(() => {
    if (atMs === null) return null;
    return {
      aircraft: replayAircraft(aircraft, trails, atMs),
      trails: replayTrails(trails, atMs, trailHours),
    };
  }, [atMs, aircraft, trails, trailHours]);
  const untimed = useMemo(() => (active ? replayUntimed(aircraft, trails) : []), [active, aircraft, trails]);
  if (!active) return null;
  return { clock, frame, untimed, dispatch: (e) => setClock((c) => (c ? replayReducer(c, e) : c)) };
}

/** The replay bar over the map. */
export function ReplayBar({
  replay,
  offsetH,
  note,
  onClose,
}: {
  readonly replay: Replay;
  readonly offsetH: number;
  /** What the replay plays, in words. */
  readonly note: string;
  readonly onClose: () => void;
}): ReactElement {
  const { clock, dispatch, untimed } = replay;
  const back = (
    <Button variant="quiet" onClick={onClose}>
      Back to live
    </Button>
  );
  if (!clock) {
    return (
      <div className="map-replay" role="region" aria-label="Replay">
        <span className="body">Nothing to replay: no aircraft trail with times in the last 48 hours.</span>
        {back}
      </div>
    );
  }
  const { range, atMs, playing, speed } = clock;
  const words = replayTimeWords(atMs, offsetH);
  const spanS = Math.max(0, Math.round((range.endMs - range.startMs) / 1000));
  return (
    <div className="map-replay" role="region" aria-label="Replay">
      <Button onClick={() => dispatch({ type: 'toggle' })}>{playing ? 'Pause' : 'Play'}</Button>
      <label className="map-replay__speed caption">
        <span>Speed</span>
        <select
          aria-label="Replay speed"
          value={speed}
          onChange={(e) => dispatch({ type: 'speed', speed: Number(e.target.value) as ReplaySpeed })}
        >
          {REPLAY_SPEEDS.map((s) => (
            <option key={s} value={s}>
              {replaySpeedWords(s)}
            </option>
          ))}
        </select>
      </label>
      <input
        className="map-replay__scrub"
        type="range"
        aria-label="Replay time"
        aria-valuetext={words}
        min={0}
        max={spanS}
        step={1}
        value={Math.round((atMs - range.startMs) / 1000)}
        onChange={(e) => dispatch({ type: 'seek', atMs: range.startMs + Number(e.target.value) * 1000 })}
      />
      <output className="data map-replay__time" aria-live="off">
        {words}
      </output>
      {back}
      <span className="caption muted map-replay__note">
        {note}
        {untimed.length > 0 && ` ${untimed.join(', ')}: the tracker sent no times, so not replayed.`}
      </span>
    </div>
  );
}
